import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { Link, Route, Switch, Redirect } from 'react-router-dom';
import { Nav, NavItem, NavLink, Row, Col, Badge } from 'reactstrap';
import classnames from 'classnames';
import BurgerMenu from '../../commons/BurgerMenu';
import Header from '../../commons/Header';
import Footer from '../../commons/Footer';
import { ReactComponent as IconRegistries } from '../../images/icn_anagrafiche.svg';
import {
  H4Styled,
  BtnRadiusWhite,
  ContFlexLeft,
  ContFlexRight,
} from '../../styled-components';
import { ReactComponent as IconBack } from '../../images/icn-back.svg';
import './ProfileDetail.scss';
import './TabsProfile.scss';
import { get } from '../../lib/fetch/profiles';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import { useAuth } from '../../commons/Auth';
import variables from '../../variables';
import NetworkAdmin from './NetworkAdmin';
import { refactoringAdminLevels } from '../../lib/helpers/refactoringAdminLevels';
import axios from 'axios';
import ProfileInfo from './ProfileInfo';
import EnergyAdmin from './EnergyAdmin';
import EnergyRecurringAdmin from './EnergyRecurringAdmin';

function UserProfileDetail(props) {
  const [{ newToken, newIsAdmin, newUserType = '' } = {}] = useAuth();
  const [t] = useTranslation('CONTACTS');
  const [profile, setProfile] = useState({});
  const [pageBack, setPageBack] = useState('');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const { history, location, match = {} } = props;
  const { params: { id } = {}, url = '' } = match;
  const { pathname = '', state = {} } = location;
  const tab = pathname.split('/').pop();
  const [managers, setManagers] = useState([]);
  const [referringAgencies, setReferringAgencies] = useState([
    { name: 'VITTORIA' },
    { name: 'ITAS' },
    { name: 'ITALIANA_ASSICURAZIONI' },
  ]);

  useEffect(() => {
    if (state.pageBack) setPageBack(state.pageBack);
  }, [state]);

  useEffect(() => {
    if (id !== 'create') {
      fetchProfile(id);
    } else {
      fetchManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchManagers = async (studioManager) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/managers`,
      {
        headers: { Authorization: newToken },
        params: studioManager ? { studioManager } : undefined,
      }
    );

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    let { data = [], headers = {} } = response;
    setManagers(data);
  };

  const fetchProfile = async (id) => {
    showLoading();

    const { error, data } = await get({ id, newToken });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    await fetchManagers(data.studioManager);
    setProfile(data);
    hideLoading();
  };

  if (pathname === url) {
    return <Redirect to={`${url}/info`} />;
  }

  const isUpdating = id !== 'create';

  const { activeAffiliation = false, type = '', businessName = '' } = profile;

  return (
    <div className="App">
      <BurgerMenu />

      <div className="corpo">
        <section>
          <Header
            title={t('PAGES:REGISTRIES')}
            icon={<IconRegistries fill={variables.white} />}
          />
        </section>
        <section>
          <Row>
            <Col sm="12" md="6">
              <ContFlexLeft>
                <BtnRadiusWhite onClick={() => history.replace(`/${pageBack}`)}>
                  <IconBack fill={variables.primary} />
                </BtnRadiusWhite>
                <H4Styled>{t('TITLE_DETAIL')}</H4Styled>
              </ContFlexLeft>
            </Col>
            {isUpdating && (
              <Col sm="12" md="6">
                <ContFlexRight>
                  <H4Styled className="registry-name">
                    <span>
                      {businessName} -{' '}
                      <small>{t(refactoringAdminLevels(type))}</small>
                    </span>
                  </H4Styled>
                  <Badge
                    className={`affiliation${
                      activeAffiliation ? ' active' : ' not-active'
                    }`}
                  >
                    {t('AFFILIATION')}
                    <br />
                    {t(`${activeAffiliation ? 'ACTIVE' : 'INACTIVE'}`)}
                  </Badge>
                </ContFlexRight>
              </Col>
            )}
          </Row>

          <div className="m-tb-20">
            <Nav tabs>
              <NavItem>
                <NavLink
                  tag={Link}
                  to={`${match.url}/info`}
                  className={classnames({ active: tab === 'info' })}
                >
                  {t('CUSTOMER_DATA')}
                </NavLink>
              </NavItem>

              {profile.type !== 'SUPPLIER' && (
                <>
                  <NavItem>
                    <NavLink
                      tag={Link}
                      to={`${match.url}/network`}
                      className={classnames({
                        active: tab === 'network',
                      })}
                    >
                      {t('COMMERCIAL:NETWORK')}
                    </NavLink>
                  </NavItem>
                </>
              )}

              {profile.type !== 'SUPPLIER' && (
                <>
                  <NavItem>
                    <NavLink
                      tag={Link}
                      to={`${match.url}/energy-admin`}
                      className={classnames({
                        active: tab === 'energy-admin',
                      })}
                    >
                      {t('ENERGY:TITLE')}
                    </NavLink>
                  </NavItem>
                </>
              )}

              {profile.type !== 'SUPPLIER' && (
                <>
                  <NavItem>
                    <NavLink
                      tag={Link}
                      to={`${match.url}/energy-admin-recurring`}
                      className={classnames({
                        active: tab === 'energy-admin-recurring',
                      })}
                    >
                      {t('ENERGY_RECURRENT:TITLE')}
                    </NavLink>
                  </NavItem>
                </>
              )}
            </Nav>

            <Switch>
              <Route
                path={`${match.url}/info`}
                component={(props) => (
                  <ProfileInfo
                    id={id}
                    profile={profile}
                    setProfile={setProfile}
                    managers={managers}
                    referringAgencies={referringAgencies}
                    {...props}
                  />
                )}
              />

              {profile.type !== 'SUPPLIER' && (
                <Route
                  path={`${match.url}/network`}
                  component={(props) => (
                    <NetworkAdmin id={id} profile={profile} {...props} />
                  )}
                />
              )}

              {profile.type !== 'SUPPLIER' && (
                <Route
                  path={`${match.url}/energy-admin`}
                  component={(props) => (
                    <EnergyAdmin id={id} profile={profile} {...props} />
                  )}
                />
              )}

              {profile.type !== 'SUPPLIER' && (
                <Route
                  path={`${match.url}/energy-admin-recurring`}
                  component={(props) => (
                    <EnergyRecurringAdmin
                      id={id}
                      profile={profile}
                      {...props}
                    />
                  )}
                />
              )}
            </Switch>
          </div>
        </section>
        <Footer />
      </div>
    </div>
  );
}

export default withRouter(UserProfileDetail);
